import { OrganizationUnit } from '~/data/orgunits';
import { RolesListReportData } from '~/data/reports';
import { Roles } from '~/data/user';
import { getLargerNumber } from '~/utils/helper';

export type RolesListReportRowResponsibleUnitDetails = {
  names: string;
  numbers: string;
};

export type RolesListReportRowRoleResponsibilities = {
  hr: RolesListReportRowResponsibleUnitDetails;
  manager: RolesListReportRowResponsibleUnitDetails;
  deputyManager: RolesListReportRowResponsibleUnitDetails;
  alternateManager: RolesListReportRowResponsibleUnitDetails;
};

export type RolesListReportRow = {
  employeeId: number;
  firstName: string;
  lastName: string;
  employeeNumber: string;
  unitId: number;
  unitName: string;
  unitNumber: string;
  managerId: number;
  username: string;
  userStatus: string;
  userRoles: string;
  responsibleUnits: RolesListReportRowRoleResponsibilities;
  hasAccount: boolean;
};

type ReportOrgUnit = {
  name: string;
  number: string;
};

export const convertRolesListReportDataToRows = (props: {
  data: RolesListReportData[];
  orgUnits: OrganizationUnit[];
  getRoleTranslations: (roles: Roles[]) => string[];
}) => {
  const { data, getRoleTranslations, orgUnits } = props;

  const getOrgUnits = (unitIds: number[] | undefined): ReportOrgUnit[] => {
    if (!Array.isArray(unitIds)) {
      return [];
    }

    return unitIds
      .map((unitId) => orgUnits.find((ou) => ou.fOrgUnitId === unitId))
      .filter((orgUnit): orgUnit is OrganizationUnit => !!orgUnit)
      .map(
        (orgUnit): ReportOrgUnit => ({
          name: orgUnit.fOrgUnitName,
          number: orgUnit.fOrgUnitNumber,
        }),
      );
  };

  const getListedOrgUnitNames = (orgUnits: ReportOrgUnit[]) =>
    orgUnits.map((u) => u.name).join(', ');
  const getListedOrgUnitNumbers = (orgUnits: ReportOrgUnit[]) =>
    orgUnits.map((u) => u.number).join(', ');

  const getResponsibleColumnsData = (unitIds: number[] | undefined) => {
    const listUnits = getOrgUnits(unitIds);

    return {
      names: getListedOrgUnitNames(listUnits),
      numbers: getListedOrgUnitNumbers(listUnits),
    };
  };

  return data.map((dataRow): RolesListReportRow => {
    const {
      fEmpId,
      fEmpFirstName,
      fEmpLastName,
      fEmpEmployeeNumber,
      fEmpUnitId,
      fEmpUnit,
      fEmpUnitNumber,
      fEmpManagerId,
      fEmpUsername,
      fEmpUserStatus,
      fEmpUserRoles,
      fEmpMgrUnits,
      fEmpDmgrUnits,
      fEmpAmgrUnits,
      fEmpHRUnits,
      fEmpHasAccount,
    } = dataRow;

    const responsibleUnits: RolesListReportRowRoleResponsibilities = {
      manager: getResponsibleColumnsData(fEmpMgrUnits),
      deputyManager: getResponsibleColumnsData(fEmpDmgrUnits),
      alternateManager: getResponsibleColumnsData(fEmpAmgrUnits),
      hr: getResponsibleColumnsData(fEmpHRUnits),
    };

    return {
      employeeId: fEmpId,
      firstName: fEmpFirstName,
      lastName: fEmpLastName,
      employeeNumber: fEmpEmployeeNumber,
      unitId: fEmpUnitId,
      unitName: fEmpUnit,
      unitNumber: fEmpUnitNumber,
      managerId: fEmpManagerId,
      username: fEmpUsername,
      userStatus: fEmpUserStatus,
      userRoles: getRoleTranslations(fEmpUserRoles).join(', '),
      responsibleUnits,
      hasAccount: fEmpHasAccount,
    };
  });
};

export type ReportResponsibleColumnWidths = {
  names: number;
  numbers: number;
};

export type ReportColumnWidths = {
  roles: number;
  hrUnits: ReportResponsibleColumnWidths;
  managerUnits: ReportResponsibleColumnWidths;
  altManagerUnits: ReportResponsibleColumnWidths;
  deputyManagerUnits: ReportResponsibleColumnWidths;
};

export const getColumnWidths = (
  reportRows: RolesListReportRow[],
): ReportColumnWidths => {
  const defaultCounts: ReportResponsibleColumnWidths = {
    names: 0,
    numbers: 0,
  };

  const charCounts = reportRows.reduce(
    (acc, row) => {
      const responsibleWidths = (
        current: ReportResponsibleColumnWidths,
        unitDetails: RolesListReportRowResponsibleUnitDetails,
      ): ReportResponsibleColumnWidths => {
        return {
          names: getLargerNumber(current.names, unitDetails.names.length),
          numbers: getLargerNumber(current.numbers, unitDetails.numbers.length),
        };
      };

      const { userRoles, responsibleUnits } = row;
      return {
        roles: getLargerNumber(acc.roles, userRoles.length),
        hrUnits: responsibleWidths(acc.hrUnits, responsibleUnits.hr),
        managerUnits: responsibleWidths(
          acc.managerUnits,
          responsibleUnits.manager,
        ),
        deputyManagerUnits: responsibleWidths(
          acc.deputyManagerUnits,
          responsibleUnits.deputyManager,
        ),
        altManagerUnits: responsibleWidths(
          acc.altManagerUnits,
          responsibleUnits.alternateManager,
        ),
      };
    },
    {
      roles: 0,
      hrUnits: defaultCounts,
      managerUnits: defaultCounts,
      altManagerUnits: defaultCounts,
      deputyManagerUnits: defaultCounts,
    } as ReportColumnWidths,
  );

  const getWidthByCharacters = (count: number) => count * 8;

  const getResponsibleWidthByCharacters = (
    responsible: ReportResponsibleColumnWidths,
  ): ReportResponsibleColumnWidths => {
    return {
      names: getWidthByCharacters(responsible.names),
      numbers: getWidthByCharacters(responsible.numbers),
    };
  };

  return {
    roles: getWidthByCharacters(charCounts.roles),
    hrUnits: getResponsibleWidthByCharacters(charCounts.hrUnits),
    managerUnits: getResponsibleWidthByCharacters(charCounts.managerUnits),
    deputyManagerUnits: getResponsibleWidthByCharacters(
      charCounts.deputyManagerUnits,
    ),
    altManagerUnits: getResponsibleWidthByCharacters(
      charCounts.altManagerUnits,
    ),
  };
};
